import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { ApiClientError } from "../lib/api/client";
import { useAuthQuery, useLoginMutation } from "../lib/api/auth";
import { Button } from "../components/ui/Button";
import { ErrorBanner } from "../components/ui/ErrorBanner";
import { FormField } from "../components/ui/FormField";
import { Logo } from "../components/Logo";

const loginSchema = z.object({
  email: z.email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuthQuery();
  const login = useLoginMutation();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  if (auth.data) {
    const redirectTo =
      (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={redirectTo} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      const redirectTo =
        (location.state as { from?: string } | null)?.from ?? "/";
      void navigate(redirectTo, { replace: true });
    } catch (error) {
      const message =
        error instanceof ApiClientError
          ? error.message
          : "Unable to sign in. Please try again.";
      setError("root", { message });
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-edlab-bg px-4">
      <div className="w-full max-w-md rounded-[15px] border border-edlab-line bg-white p-8 shadow-[0_3px_10px_#303c4e03]">
        <div className="mb-8 text-center">
          <Logo size="lg" className="mx-auto mb-4" />
          <h1 className="text-2xl font-semibold tracking-tight text-edlab-ink">
            Sign in to EDLAB FMS
          </h1>
          <p className="mt-2 text-sm text-edlab-muted">
            Use your assigned account credentials.
          </p>
        </div>

        <form className="grid gap-4" onSubmit={(event) => void onSubmit(event)}>
          {errors.root ? <ErrorBanner message={errors.root.message ?? ""} /> : null}

          <FormField label="Email" error={errors.email?.message}>
            <input
              type="email"
              autoComplete="email"
              className="edlab-input"
              {...register("email")}
            />
          </FormField>

          <FormField label="Password" error={errors.password?.message}>
            <input
              type="password"
              autoComplete="current-password"
              className="edlab-input"
              {...register("password")}
            />
          </FormField>

          <Button
            type="submit"
            disabled={isSubmitting || login.isPending}
            className="mt-2 w-full"
          >
            {isSubmitting || login.isPending ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-edlab-muted">
          <Link to="/forgot-password" className="text-edlab-green hover:underline">
            Forgot password?
          </Link>
        </p>

        {import.meta.env.DEV ? (
          <p className="edlab-notice mt-4 text-xs">
            Local dev: admin@edlab.local · password from DEV_ADMIN_PASSWORD in .env
          </p>
        ) : null}
      </div>
    </div>
  );
}
